import { useEffect, useState } from "react";

import {
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";
import { getUserID } from "~/visits/utils/visitCookies.js";

import {
  EXPERIMENT_CHECKOUT_KLARNA,
  VARIANT_CONTROL,
  VARIANT_TEST,
} from "../experimentKeys";
import { useExperimentVariant } from "../useExperimentVariant";

export const KLARNA_CHECKOUT_EXPERIMENT = EXPERIMENT_CHECKOUT_KLARNA;

// The checkouts the server can offer Klarna on: a visit submitted for clinical
// review (initial and follow-up anxiety, initial insomnia), never a refill, which
// charges against an already-closed visit. Mirrors app.KlarnaCheckoutEligible
// (app/domain_klarna_checkout.go) -- keep the two in sync. The server is the
// authority and still checks the product catalog, the visit and its switch; this
// copy exists so a checkout that could never see Klarna never asks for the flag,
// and so never records an exposure that would dilute the experiment.
const KLARNA_CONSULTATION_TYPES = [
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
];

export function klarnaCheckoutMayBeOffered(consultationType, orderType) {
  return (
    typeof orderType === "string" &&
    orderType !== "" &&
    !orderType.endsWith("-refill") &&
    KLARNA_CONSULTATION_TYPES.includes(consultationType)
  );
}

// How long the payment step waits for PostHog before it gives up and loads the
// control form. posthog-js gives the flag request itself 3000 ms
// (feature_flag_request_timeout_ms) before failing, so this MUST be longer: if we
// settled first, a slow-but-successful flag load would build a card-only intent
// while PostHog records a test exposure, and the experiment would be measuring an
// experience the patient never saw.
const FLAG_FALLBACK_MS = 4000;

// One decision per patient per checkout session, kept for the life of the page.
// See useKlarnaCheckoutExperiment for why it is never revised once made.
const decisions = new Map();

// useKlarnaCheckoutExperiment decides whether this checkout is offered Klarna in
// the Stripe Payment Element. It is the only place that answers that product
// question: the payment step reads `offerKlarna`, sends the matching arm on
// /setup-intent, and the server adds Klarna to the SetupIntent for the test arm
// only. Nothing is stored about the assignment on our side.
//
// THE DECISION COMES FIRST. The Payment Element shows exactly the methods its
// SetupIntent was created with, so the arm has to be known before the intent is
// requested. The payment step therefore waits on `ready` before asking for the
// intent -- the one experiment on this site that holds a screen -- bounded by
// FLAG_FALLBACK_MS, after which the checkout settles on control.
//
// ELIGIBILITY IS CHECKED HERE. Only the consultation types the server can offer
// Klarna on, and never a refill, ask for the flag (klarnaCheckoutMayBeOffered). A
// checkout without a user id is also left out: the funnel joins the browser
// exposure to the server-sent Visit Checkout / Purchase events through the
// identified person (the visits bundle identifies the user id on load), and an
// anonymous exposure could never join.
//
// EXPOSURE IS NATIVE. useExperimentVariant records $feature_flag_called when it
// resolves the flag; the experiment's funnels run from that exposure through the
// server events. consultationType and orderType ride along as session properties
// so results can be read per product.
//
// THE DECISION IS FROZEN. The first settled value for a (user, checkout session)
// is kept in a module-level map: a "Change payment method" reload of the intent,
// a remount, or a flag refresh must not move a patient between arms mid-checkout.
// Once decided, the hook stops listening for flags: a flag answer arriving after
// the fallback would otherwise still call getFeatureFlag and record an exposure
// for an arm the patient never saw.
//
// Returns { ready, offerKlarna }. `offerKlarna` is true only for an eligible
// checkout frozen on the test variant. Holdout and unknown values read as control.
export function useKlarnaCheckoutExperiment({
  checkoutSessionID,
  consultationType,
  orderType,
}) {
  const userID = getUserID();
  const eligible = klarnaCheckoutMayBeOffered(consultationType, orderType);
  const active = Boolean(eligible && userID && checkoutSessionID);
  const key = `${userID}:${checkoutSessionID}`;

  const [decision, setDecision] = useState(() => decisions.get(key));
  const frozen = decision?.key === key ? decision : undefined;

  const { ready: flagsReady, variant } = useExperimentVariant(
    KLARNA_CHECKOUT_EXPERIMENT,
    {
      eligible: active && !frozen,
      exposureProperties: eligible
        ? { consultation_type: consultationType, order_type: orderType }
        : undefined,
    },
  );

  useEffect(() => {
    if (!active) return undefined;
    if (decisions.has(key)) {
      setDecision(decisions.get(key));
      return undefined;
    }

    let canceled = false;
    const settle = (settledVariant) => {
      if (canceled || decisions.has(key)) return;
      const settled = { key, variant: settledVariant };
      decisions.set(key, settled);
      setDecision(settled);
    };

    if (flagsReady) {
      settle(variant);
      return undefined;
    }

    // Klarna is an extra option; a slow flag service must not hold up checkout.
    const timer = setTimeout(() => settle(VARIANT_CONTROL), FLAG_FALLBACK_MS);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [active, key, flagsReady, variant]);

  return {
    ready: !active || Boolean(frozen),
    offerKlarna: Boolean(active && frozen?.variant === VARIANT_TEST),
  };
}
